export type WorldKind = 'pink' | 'black';
export type SpeciesId = 'crystal' | 'mist' | 'thorn' | 'fluff' | 'fluid' | 'light' | 'myco';

export type TraitKey =
  | 'curiosity'
  | 'vigilance'
  | 'attachment'
  | 'sociability'
  | 'pride'
  | 'mimicry'
  | 'territoriality'
  | 'pairBond'
  | 'calmness'
  | 'volatility'
  | 'energy'
  | 'sensitivity';

export type AffectKey =
  | 'joy'
  | 'valence'
  | 'arousal'
  | 'dominance'
  | 'trust'
  | 'belonging'
  | 'curiosity'
  | 'safety'
  | 'hunger'
  | 'energy'
  | 'sleepPressure'
  | 'socialNeed';

export type ActionId = string;

export interface Traits extends Record<TraitKey, number> {
  curiosity: number;
  vigilance: number;
  attachment: number;
  sociability: number;
  pride: number;
  mimicry: number;
  territoriality: number;
  pairBond: number;
  calmness: number;
  volatility: number;
  energy: number;
  sensitivity: number;
}

export interface AffectState extends Record<AffectKey, number> {
  joy: number;
  valence: number;
  arousal: number;
  dominance: number;
  trust: number;
  belonging: number;
  curiosity: number;
  safety: number;
  hunger: number;
  energy: number;
  sleepPressure: number;
  socialNeed: number;
}

export interface HeartbeatState {
  bpm: number;
  phase: number;
  energy: number;
  hunger: number;
  sleepPressure: number;
  homeostasis: number;
  lastTick: number;
}

export interface IdentityState {
  id: string;
  displayName: string;
  species: SpeciesId;
  generation: number;
  adoptedAt?: number;
  pronouns?: string;
  origin: string;
  tags: string[];
}

export interface SoulState {
  traits: Traits;
  affect: AffectState;
  memories: MemoryTrace[];
  preferences: PreferenceTrace[];
  attachmentScore: number;
  morphologyDrift: number;
}

export interface BotGenome {
  seed: number;
  baseScale: number;
  hue: number;
  accent: string;
  pattern: 'ring' | 'spot' | 'line' | 'crystal' | 'mycelium' | 'glow' | 'thorn';
  appendages: number;
}

export interface BotProfile {
  identity: IdentityState;
  genome: BotGenome;
  soul: SoulState;
  heartbeat: HeartbeatState;
  portrait: string;
  tagline: string;
  bodyNote: string;
  habitatAffinity: string[];
  signatureActions: string[];
  canonical: boolean;
}

export interface MemoryTrace {
  id: string;
  kind: 'interaction' | 'observation' | 'social' | 'ritual' | 'reward' | 'boundary';
  cue: string;
  valence: number;
  intensity: number;
  createdAt: number;
  decay: number;
}

export interface PreferenceTrace {
  cue: string;
  score: number;
  confidence: number;
  samples: number;
  lastSeen: number;
}

export interface Observation {
  source: 'pointer' | 'screen' | 'user' | 'world' | 'social' | 'system';
  cue: string;
  intensity?: number;
  valence?: number;
  zone?: string;
  metadata?: Record<string, string | number | boolean>;
  at?: number;
}

export interface BehaviorDecision {
  action: ActionId;
  reason: string;
  confidence: number;
  target?: string;
  expiresAt: number;
}

export interface WorldProp {
  id: string;
  kind: 'surface' | 'edge' | 'text' | 'image' | 'window' | 'video' | 'food' | 'mirror' | 'water' | 'portal' | 'void';
  x: number;
  y: number;
  width: number;
  height: number;
  material: 'glass' | 'paper' | 'metal' | 'liquid' | 'soft' | 'light' | 'dark';
  affordances: string[];
  source?: string;
}

export interface SocialSignal {
  from: string;
  to: string;
  kind: 'greet' | 'follow' | 'avoid' | 'compete' | 'protect' | 'imitate' | 'bond' | 'jealousy';
  strength: number;
  at: number;
}

export interface CompanionEvent {
  id: string;
  botId: string;
  type: string;
  payload: Record<string, unknown>;
  at: number;
  synced: boolean;
}

export interface PersistedBotState {
  profile: BotProfile;
  eventCursor: number;
  updatedAt: number;
}

export const AFFECT_KEYS: AffectKey[] = [
  'joy', 'valence', 'arousal', 'dominance', 'trust', 'belonging',
  'curiosity', 'safety', 'hunger', 'energy', 'sleepPressure', 'socialNeed',
];

export const TRAIT_KEYS: TraitKey[] = [
  'curiosity', 'vigilance', 'attachment', 'sociability', 'pride', 'mimicry',
  'territoriality', 'pairBond', 'calmness', 'volatility', 'energy', 'sensitivity',
];

export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

export function createDefaultAffect(): AffectState {
  return {
    joy: 0.42, valence: 0.16, arousal: 0.32, dominance: 0.38,
    trust: 0.24, belonging: 0.1, curiosity: 0.52, safety: 0.62,
    hunger: 0.18, energy: 0.72, sleepPressure: 0.08, socialNeed: 0.28,
  };
}
